import { LocationOn } from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  CircularProgress,
  TextField,
  createFilterOptions,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import InputField from "components/Input/InputField";
import TextArea from "components/Input/TextArea";
import { months, programLevel, programType, states } from "const/constants";
import { useCourseSearch } from "hooks/useCourseSearch";
import { useEffect, useMemo, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import { MdAdd, MdDelete, MdSave, MdPublic, MdLock } from "react-icons/md";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

const initialCourseFormData = {
  uniCourseId: null,
  courseId: null,
  courseName: "",
  programLevel: [],
  field: "",
  websiteLink: "",
  durationInWeeks: "",
  noOfSem: "",
  intakes: [],
  enrollmentFee: "",
  materialFee: "",
  onshoreFees: [],
  offshoreFees: {
    feePerSem: "",
    totalFees: "",
    scholarship: "",
    feeAfterScholarship: "",
    discount: "",
    materialFee: "",
    offShoreKey1: "",
    enrollmentFee: "",
  },
  englishLanguageRequirements: "",
  academicRequirements: "",
  otherRequirements: "",
  key1: "",
};

const Section = ({ title, description, icon, action, children }) => (
  <section className="rounded-xl border border-app-border bg-app-surface shadow-sm">
    <header className="flex flex-col gap-2 border-b border-app-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        {icon && (
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-app-surface-hover text-primary">
            {icon}
          </div>
        )}
        <div>
          <h2 className="text-base font-semibold text-app-text">{title}</h2>
          {description && <p className="mt-0.5 text-sm text-app-text-muted">{description}</p>}
        </div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </header>
    <div className="px-5 py-5">{children}</div>
  </section>
);

const AddUniCourse = () => {
  const navigate = useNavigate();
  const { uniId, courseId } = useParams();
  const { state } = useLocation();
  const isEdit = Boolean(courseId);
  const filter = createFilterOptions();

  const [universityName, setUniversityName] = useState(state?.universityName || "");
  const [courseFormData, setCourseFormData] = useState(initialCourseFormData);

  const {
    inputValue: courseNameInput,
    setInputValue: setCourseNameInput,
    options: courseNameOptions,
    loading: courseNameLoading,
    listboxProps: courseNameListboxProps,
  } = useCourseSearch({ selectedCourse: null });

  useEffect(() => {
    if (courseFormData.courseName && !courseNameInput) {
      setCourseNameInput(courseFormData.courseName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseFormData.courseName]);
  const [showOffshoreFee, setShowOffshoreFee] = useState(false);
  const [orgOnly, setOrgOnly] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (universityName || !uniId) return;
    axios
      .get(`/university/${uniId}`)
      .then((res) => {
        if (res?.data?.data?.name) setUniversityName(res.data.data.name);
      })
      .catch(() => {});
  }, [uniId]);

  useEffect(() => {
    if (!isEdit) {
      setCourseFormData(initialCourseFormData);
      return;
    }
    setLoading(true);
    axios
      .get(`/university/uniCourse/${courseId}`)
      .then((res) => {
        const data = res?.data?.data;
        if (!data) {
          toast.error("Could not load course data");
          return;
        }
        if (!data.onshoreFees || data.onshoreFees.length === 0) {
          data.onshoreFees = [
            {
              state: "New South Wales",
              feePerSem: data.feePerSem || "",
              totalFees: data.totalFees || "",
              scholarship: data.scholarship || "",
              feeAfterScholarship: data.feeAfterScholarship || "",
              discount: data.discount || "",
              key1: "",
              uuid: crypto.randomUUID(),
            },
          ];
        }
        if (data?.offshoreFees?.feePerSem) setShowOffshoreFee(true);
        setCourseFormData({
          ...initialCourseFormData,
          ...data,
          uniCourseId: data?.courseId || courseId,
          courseId: null,
        });
      })
      .catch(() => toast.error("Error fetching course data"))
      .finally(() => setLoading(false));
  }, [courseId, isEdit]);

  const handleField = (field, value) => {
    setCourseFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleOffshore = (field, value) => {
    setCourseFormData((prev) => ({
      ...prev,
      offshoreFees: { ...(prev.offshoreFees || {}), [field]: value },
    }));
  };

  const updateOnshore = (state, field, value) => {
    setCourseFormData((prev) => ({
      ...prev,
      onshoreFees: prev.onshoreFees.map((fee) =>
        fee.state === state ? { ...fee, [field]: value } : fee
      ),
    }));
  };

  const copyStateFees = (sourceState, targetState) => {
    setCourseFormData((prev) => {
      const sourceFees = prev.onshoreFees.find((fee) => fee.state === sourceState);
      if (!sourceFees) return prev;
      return {
        ...prev,
        onshoreFees: prev.onshoreFees.map((fee) =>
          fee.state === targetState ? { ...sourceFees, state: targetState } : fee
        ),
      };
    });
  };

  const removeOffshoreFee = () => {
    setShowOffshoreFee(false);
    setCourseFormData((prev) => ({
      ...prev,
      offshoreFees: {
        feePerSem: "",
        totalFees: "",
        scholarship: "",
        feeAfterScholarship: "",
        discount: "",
        materialFee: "",
        offShoreKey1: "",
        enrollmentFee: "",
      },
    }));
  };

  const copyToOffshore = () => {
    setCourseFormData((prev) => ({
      ...prev,
      offshoreFees: {
        ...(prev.offshoreFees || {}),
        feePerSem: prev?.onshoreFees?.[0]?.feePerSem || "",
        totalFees: prev?.onshoreFees?.[0]?.totalFees || "",
        scholarship: prev?.onshoreFees?.[0]?.scholarship || "",
        feeAfterScholarship: prev?.onshoreFees?.[0]?.feeAfterScholarship || "",
        discount: prev?.onshoreFees?.[0]?.discount || "",
        key1: prev?.onshoreFees?.[0]?.key1 || "",
      },
    }));
    toast.success("Onshore fees copied to offshore");
  };

  const extendedStateOptions = useMemo(() => {
    const customStates =
      courseFormData?.onshoreFees
        ?.filter((fee) => fee.state && !states?.some((s) => s.value === fee.state))
        .map((fee) => ({ value: fee.state, label: fee.state })) || [];
    return [...states, ...customStates];
  }, [courseFormData]);

  const navigateBack = () => {
    if (uniId) {
      if (state?.from === "course") navigate(-1);
      else navigate(`/admin/university/edit/${uniId}`);
    } else navigate(-1);
  };

  const { mutate: createCourse, isLoading: isCreating } = useMutation(
    async (payload) => axios.post(`/university/${uniId}/course`, payload),
    {
      onSuccess() {
        toast.success("Course added successfully");
        navigateBack();
      },
      onError() {
        toast.error("Error submitting course");
      },
    }
  );

  const { mutate: updateCourse, isLoading: isUpdating } = useMutation(
    async (payload) =>
      axios.put(`/university/${uniId}/course/${courseFormData?.uniCourseId}`, payload),
    {
      onSuccess() {
        toast.success("Course updated successfully");
        navigateBack();
      },
      onError() {
        toast.error("Error updating course");
      },
    }
  );

  const isSaving = isCreating || isUpdating;

  const handleSave = () => {
    if (!courseFormData?.courseName?.trim()) {
      toast.error("Please enter a course name");
      return;
    }
    if (!uniId) {
      toast.error("Missing university id");
      return;
    }
    const payload = {
      ...courseFormData,
      intakes: courseFormData?.intakes?.map((intake) => intake.value ?? intake) || [],
      ...(isEdit ? {} : { orgOnly }),
    };
    if (isEdit && courseFormData?.uniCourseId) {
      updateCourse(payload);
    } else {
      createCourse(payload);
    }
  };

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-app-surface-muted">
      <div className="sticky top-0 z-20 border-b border-app-border bg-app-surface">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-4 sm:px-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <button
                type="button"
                onClick={navigateBack}
                className="mb-1 inline-flex items-center gap-1.5 text-xs font-medium text-app-text-muted hover:text-app-text"
              >
                <IoArrowBack /> Back to {universityName || "university"}
              </button>
              <h1 className="truncate text-xl font-semibold tracking-tight text-app-text">
                {isEdit ? "Edit course" : "Add new course"}
              </h1>
              <p className="mt-0.5 truncate text-sm text-app-text-muted">
                {universityName ? (
                  <>
                    For <span className="font-medium text-app-text">{universityName}</span>
                  </>
                ) : (
                  "Configure course details, fees and requirements."
                )}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                variant="outlined"
                color="inherit"
                onClick={navigateBack}
                disabled={isSaving}
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={isSaving}
                startIcon={
                  isSaving ? <CircularProgress size={18} sx={{ color: "white" }} /> : <MdSave />
                }
                sx={{ textTransform: "none" }}
              >
                {isEdit ? "Save changes" : "Create course"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[1200px] space-y-5 px-4 py-6 sm:px-6">
          <Section
            title="Basic information"
            description="Course identity, level, type and schedule."
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="md:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-app-text-secondary">
                  Course name *
                </label>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={courseNameOptions}
                  loading={courseNameLoading}
                  value={courseFormData?.courseName || null}
                  inputValue={courseNameInput}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === "reset") return;
                    setCourseNameInput(newInputValue);
                    setCourseFormData((prev) => ({ ...prev, courseName: newInputValue }));
                  }}
                  onChange={(_, newValue) => {
                    const name =
                      typeof newValue === "string"
                        ? newValue
                        : newValue?.inputValue || newValue?.courseName || "";
                    setCourseFormData((prev) => ({ ...prev, courseName: name }));
                    setCourseNameInput(name);
                  }}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option?.courseName || ""
                  }
                  isOptionEqualToValue={(o, v) => o?.courseName === v?.courseName}
                  filterOptions={(options, params) => {
                    const { inputValue } = params;
                    if (inputValue !== "" && !options.some((o) => o.courseName === inputValue)) {
                      return [...options, { inputValue, courseName: `Add "${inputValue}"` }];
                    }
                    return options;
                  }}
                  selectOnFocus
                  clearOnBlur
                  handleHomeEndKeys
                  renderInput={(p) => (
                    <TextField
                      {...p}
                      placeholder="Enter or select a course"
                      fullWidth
                      InputProps={{
                        ...p.InputProps,
                        endAdornment: (
                          <>
                            {courseNameLoading ? (
                              <CircularProgress color="inherit" size={16} />
                            ) : null}
                            {p.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  ListboxProps={{ style: { maxHeight: "50vh" }, ...courseNameListboxProps }}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-app-text-secondary">
                  Program level *
                </label>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={programLevel || []}
                  value={
                    courseFormData.programLevel?.length > 0
                      ? programLevel?.find((c) => c?.value === courseFormData.programLevel[0]) ||
                        null
                      : null
                  }
                  onChange={(_, v) =>
                    setCourseFormData((prev) => ({
                      ...prev,
                      programLevel: v?.value ? [v.value] : [],
                    }))
                  }
                  isOptionEqualToValue={(o, v) => o?.value === v?.value}
                  renderInput={(p) => <TextField {...p} placeholder="Bachelor, Master..." />}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-app-text-secondary">
                  Program type *
                </label>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={programType || []}
                  value={programType?.find((c) => c?.value === courseFormData.field) || null}
                  onChange={(_, v) => handleField("field", v?.value || v || "")}
                  isOptionEqualToValue={(o, v) => o?.value === v?.value}
                  renderInput={(p) => <TextField {...p} placeholder="Select program type" />}
                />
              </div>

              <InputField
                label="Website link"
                name="websiteLink"
                value={courseFormData.websiteLink || ""}
                onChange={(e) => handleField("websiteLink", e.target.value)}
              />
              <InputField
                label="Duration (weeks)"
                name="durationInWeeks"
                type="number"
                value={courseFormData.durationInWeeks || ""}
                onChange={(e) => handleField("durationInWeeks", e.target.value)}
              />
              <InputField
                label="Number of semesters"
                name="noOfSem"
                type="number"
                value={courseFormData.noOfSem || ""}
                onChange={(e) => handleField("noOfSem", e.target.value)}
              />

              <div className="md:col-span-3">
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-app-text-secondary">
                  Available intakes
                </label>
                <Autocomplete
                  multiple
                  size="small"
                  options={months || []}
                  value={courseFormData.intakes || []}
                  onChange={(_, v) => handleField("intakes", v)}
                  isOptionEqualToValue={(o, v) => o.value === (v?.value ?? v)}
                  disableCloseOnSelect
                  renderInput={(p) => <TextField {...p} placeholder="Pick intake months" />}
                  ListboxProps={{ style: { maxHeight: 220 } }}
                />
              </div>

              {!isEdit && (
                <div className="md:col-span-3">
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-app-text-secondary">
                    Who can see this course offering?
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setOrgOnly(false)}
                      className={`flex items-start gap-2 rounded-lg border p-3 text-left transition-colors duration-150 ${
                        !orgOnly
                          ? "border-blue-500 bg-blue-50"
                          : "border-app-border bg-app-surface hover:bg-app-surface-alt"
                      }`}
                    >
                      <MdPublic
                        className={`mt-0.5 w-4 h-4 flex-shrink-0 ${
                          !orgOnly ? "text-blue-600" : "text-app-text-muted"
                        }`}
                      />
                      <span>
                        <span
                          className={`block text-sm font-medium ${
                            !orgOnly ? "text-blue-800" : "text-app-text"
                          }`}
                        >
                          Shared with every organization
                        </span>
                        <span className="block text-xs text-app-text-muted mt-0.5">
                          Default. Any organization offering this university can use it.
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setOrgOnly(true)}
                      className={`flex items-start gap-2 rounded-lg border p-3 text-left transition-colors duration-150 ${
                        orgOnly
                          ? "border-blue-500 bg-blue-50"
                          : "border-app-border bg-app-surface hover:bg-app-surface-alt"
                      }`}
                    >
                      <MdLock
                        className={`mt-0.5 w-4 h-4 flex-shrink-0 ${
                          orgOnly ? "text-blue-600" : "text-app-text-muted"
                        }`}
                      />
                      <span>
                        <span
                          className={`block text-sm font-medium ${
                            orgOnly ? "text-blue-800" : "text-app-text"
                          }`}
                        >
                          Only my organization
                        </span>
                        <span className="block text-xs text-app-text-muted mt-0.5">
                          Hidden from every other organization, even if they can see this
                          university.
                        </span>
                      </span>
                    </button>
                  </div>
                  <p className="text-xs text-app-text-muted mt-2">
                    Independent of the university's own sharing setting. Applies only at creation —
                    can't be changed later from this form.
                  </p>
                </div>
              )}
            </div>
          </Section>

          <Section
            title="Extra fees"
            description="One-time charges that aren't tied to a specific state."
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <InputField
                label="Enrolment fee"
                name="enrollmentFee"
                type="number"
                value={courseFormData.enrollmentFee || ""}
                onChange={(e) => handleField("enrollmentFee", e.target.value)}
              />
              <InputField
                label="Material fee"
                name="materialFee"
                type="number"
                value={courseFormData.materialFee || ""}
                onChange={(e) => handleField("materialFee", e.target.value)}
              />
            </div>
          </Section>

          <Section
            title="Onshore fees"
            description="Fees charged for students studying inside Australia. Add a row per state."
            icon={<LocationOn fontSize="small" />}
          >
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-app-text-secondary">
                  States offered
                </label>
                <Autocomplete
                  multiple
                  size="small"
                  options={extendedStateOptions}
                  value={extendedStateOptions.filter((stateOption) =>
                    courseFormData?.onshoreFees?.some((fee) => fee.state === stateOption.value)
                  )}
                  onChange={(_, value) => {
                    const newOnshoreFees = value.map((stateOption) => {
                      const existing = courseFormData?.onshoreFees?.find(
                        (fee) => fee.state === stateOption.value
                      );
                      if (existing) return existing;
                      return {
                        feePerSem: "",
                        totalFees: "",
                        scholarship: "",
                        feeAfterScholarship: "",
                        discount: "",
                        state:
                          typeof stateOption === "string"
                            ? stateOption
                            : stateOption?.inputValue || stateOption?.value,
                        uuid: crypto.randomUUID(),
                        key1: "",
                      };
                    });
                    setCourseFormData((prev) => ({ ...prev, onshoreFees: newOnshoreFees }));
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);
                    const { inputValue } = params;
                    if (inputValue !== "" && !options.some((o) => o.label === inputValue)) {
                      filtered.push({ inputValue, label: `Add "${inputValue}"` });
                    }
                    return filtered;
                  }}
                  isOptionEqualToValue={(o, v) => o.value === v.value}
                  getOptionLabel={(o) => o.label}
                  renderInput={(p) => <TextField {...p} placeholder="Select states" />}
                />
              </div>

              {courseFormData?.onshoreFees?.length > 0 ? (
                <div className="space-y-3">
                  {courseFormData.onshoreFees.map((fee) => (
                    <div
                      key={fee.uuid || fee.state}
                      className="rounded-lg border border-app-border bg-app-surface-muted/60 p-4"
                    >
                      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 rounded-md bg-app-surface-hover px-2 py-1 text-xs font-semibold text-sky-700">
                            <LocationOn fontSize="inherit" /> {fee.state}
                          </span>
                          <span className="text-xs text-app-text-muted">state-wise fees</span>
                        </div>
                        {courseFormData.onshoreFees.length > 1 && (
                          <Autocomplete
                            size="small"
                            options={courseFormData.onshoreFees.filter(
                              (f) => f.state !== fee.state
                            )}
                            getOptionLabel={(o) => o.state}
                            onChange={(_, value) => value && copyStateFees(value.state, fee.state)}
                            sx={{ width: 220 }}
                            renderInput={(p) => (
                              <TextField {...p} label="Copy from state" size="small" />
                            )}
                          />
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <InputField
                          label="Fee per semester"
                          name="feePerSem"
                          type="number"
                          value={fee.feePerSem || ""}
                          onChange={(e) => updateOnshore(fee.state, "feePerSem", e.target.value)}
                        />
                        <InputField
                          label="Total fees"
                          name="totalFees"
                          type="number"
                          value={fee.totalFees || ""}
                          onChange={(e) => updateOnshore(fee.state, "totalFees", e.target.value)}
                        />
                        <InputField
                          label="Scholarship (%)"
                          name="scholarship"
                          type="number"
                          value={fee.scholarship || ""}
                          onChange={(e) => updateOnshore(fee.state, "scholarship", e.target.value)}
                        />
                        <InputField
                          label="Per sem after scholarship"
                          name="perSemFeeAfterScholarship"
                          type="number"
                          value={
                            Number(fee.feePerSem) -
                              (Number(fee.scholarship) / 100) * Number(fee.feePerSem) || ""
                          }
                          disabled
                          fixedDigit
                        />
                        <InputField
                          label="Total after scholarship"
                          name="feeAfterScholarship"
                          type="number"
                          value={
                            Number(fee.totalFees) -
                              (Number(fee.scholarship) / 100) * Number(fee.totalFees) || ""
                          }
                          disabled
                          fixedDigit
                        />
                        <InputField
                          label="Discount (%)"
                          name="discount"
                          type="number"
                          value={fee.discount || ""}
                          onChange={(e) => updateOnshore(fee.state, "discount", e.target.value)}
                        />
                        <div className="md:col-span-3">
                          <InputField
                            label="Note"
                            name="key1"
                            value={fee.key1 || ""}
                            onChange={(e) => updateOnshore(fee.state, "key1", e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="rounded-lg border border-dashed border-app-border bg-app-surface px-4 py-6 text-center text-sm text-app-text-muted">
                  Select one or more states above to configure onshore fees.
                </p>
              )}
            </div>
          </Section>

          <Section
            title="Offshore fees"
            description="Fees charged for students applying from outside Australia."
            action={
              <div className="flex items-center gap-2">
                {showOffshoreFee && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={copyToOffshore}
                    sx={{ textTransform: "none" }}
                  >
                    Copy from onshore
                  </Button>
                )}
                <Button
                  variant={showOffshoreFee ? "outlined" : "contained"}
                  color={showOffshoreFee ? "error" : "primary"}
                  size="small"
                  startIcon={showOffshoreFee ? <MdDelete /> : <MdAdd />}
                  onClick={showOffshoreFee ? removeOffshoreFee : () => setShowOffshoreFee(true)}
                  sx={{ textTransform: "none" }}
                >
                  {showOffshoreFee ? "Remove" : "Add offshore fees"}
                </Button>
              </div>
            }
          >
            {showOffshoreFee ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <InputField
                  label="Fee per semester"
                  type="number"
                  value={courseFormData?.offshoreFees?.feePerSem || ""}
                  onChange={(e) => handleOffshore("feePerSem", e.target.value)}
                />
                <InputField
                  label="Total fees"
                  type="number"
                  value={courseFormData?.offshoreFees?.totalFees || ""}
                  onChange={(e) => handleOffshore("totalFees", e.target.value)}
                />
                <InputField
                  label="Scholarship (%)"
                  type="number"
                  value={courseFormData?.offshoreFees?.scholarship || ""}
                  onChange={(e) => handleOffshore("scholarship", e.target.value)}
                />
                <InputField
                  label="Per sem after scholarship"
                  type="number"
                  value={
                    Number(courseFormData?.offshoreFees?.feePerSem) -
                      (Number(courseFormData?.offshoreFees?.scholarship) / 100) *
                        Number(courseFormData?.offshoreFees?.feePerSem) || ""
                  }
                  disabled
                  fixedDigit
                />
                <InputField
                  label="Total after scholarship"
                  type="number"
                  value={
                    Number(courseFormData?.offshoreFees?.totalFees) -
                      (Number(courseFormData?.offshoreFees?.scholarship) / 100) *
                        Number(courseFormData?.offshoreFees?.totalFees) || ""
                  }
                  disabled
                  fixedDigit
                />
                <InputField
                  label="Material fee"
                  type="number"
                  value={courseFormData?.offshoreFees?.materialFee || ""}
                  onChange={(e) => handleOffshore("materialFee", e.target.value)}
                />
                <InputField
                  label="Discount (%)"
                  type="number"
                  value={courseFormData?.offshoreFees?.discount || ""}
                  onChange={(e) => handleOffshore("discount", e.target.value)}
                />
                <div className="md:col-span-3">
                  <InputField
                    label="Note"
                    value={courseFormData?.offshoreFees?.offShoreKey1 || ""}
                    onChange={(e) => handleOffshore("offShoreKey1", e.target.value)}
                  />
                </div>
              </div>
            ) : (
              <p className="rounded-lg border border-dashed border-app-border bg-app-surface px-4 py-6 text-center text-sm text-app-text-muted">
                No offshore fees configured. Add them if this course is also offered to
                international applicants.
              </p>
            )}
          </Section>

          <Section
            title="Requirements & outcomes"
            description="Tell admins what students need, and what they walk away with."
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <InputField
                label="English language requirements"
                name="englishLanguageRequirements"
                value={courseFormData?.englishLanguageRequirements || ""}
                onChange={(e) => handleField("englishLanguageRequirements", e.target.value)}
              />
              <InputField
                label="Academic requirements"
                name="academicRequirements"
                value={courseFormData?.academicRequirements || ""}
                onChange={(e) => handleField("academicRequirements", e.target.value)}
              />
              <TextArea
                label="Other requirements"
                name="otherRequirements"
                rows={4}
                value={courseFormData?.otherRequirements || ""}
                onChange={(e) => handleField("otherRequirements", e.target.value)}
              />
              <TextArea
                label="Course outcomes"
                name="key1"
                rows={4}
                value={courseFormData?.key1 || ""}
                onChange={(e) => handleField("key1", e.target.value)}
              />
            </div>
          </Section>

          <div className="flex flex-col-reverse items-center justify-end gap-2 sm:flex-row">
            <Button
              variant="outlined"
              color="inherit"
              onClick={navigateBack}
              disabled={isSaving}
              sx={{ textTransform: "none", minWidth: 120 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={isSaving}
              startIcon={
                isSaving ? <CircularProgress size={18} sx={{ color: "white" }} /> : <MdSave />
              }
              sx={{ textTransform: "none", minWidth: 160 }}
            >
              {isEdit ? "Save changes" : "Create course"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUniCourse;
